/**
 * Where an open action menu goes (PRD CON-001, NFR-005).
 *
 * The list is `position: fixed`, so it is placed against the VIEWPORT and not
 * against the document: nothing it overflows can be scrolled to, because a fixed
 * element adds nothing to any scroll area. Dropping it below its trigger and
 * hoping is therefore not a placement - for a row low in a long portfolio every
 * entry lands past the bottom of the window, and CON-001 puts the only route to
 * the reprint, the revoke-and-reissue, the guest
 * preview (CON-003) and the servability explanation (GST-040) in that menu.
 *
 * So the arithmetic lives here, pure and measured in plain numbers: it flips the
 * list above the trigger when what is below cannot hold it, clamps it inside the
 * viewport on both axes, and - when even the taller side is too short - caps its
 * height so the entries that do not fit are reached by scrolling the menu rather
 * than by scrolling a page that will not move.
 */

/** Between the trigger and the list. */
const GAP = 4;
/** Between the list and the edge of the window. */
const MARGIN = 8;
/**
 * Never squeeze the list below this. A menu clipped to 20px is unusable; better
 * to overlap the trigger and stay whole, which the viewport clamp then keeps on
 * screen.
 */
const MIN_HEIGHT = 96;

export interface MenuGeometry {
  /** The trigger, in viewport coordinates. */
  readonly anchorTop: number;
  readonly anchorBottom: number;
  /**
   * Where the list is sitting NOW, transform included: the `--end` alignment
   * shifts the box left by its own width in CSS, so the inline coordinate and
   * the painted edge are not the same number.
   */
  readonly listLeft: number;
  readonly listWidth: number;
  /** The list's natural height, before any cap this function applies. */
  readonly listHeight: number;
  /** The inline `left` that produced `listLeft`. */
  readonly inlineLeft: number;
  readonly viewportWidth: number;
  readonly viewportHeight: number;
}

export interface MenuPlacement {
  readonly top: number;
  readonly left: number;
  /** Set only when the list had to be capped; the list scrolls itself then. */
  readonly maxHeight: number | null;
}

const clamp = (value: number, low: number, high: number): number =>
  Math.min(Math.max(value, low), Math.max(low, high));

export function placeMenu(geometry: MenuGeometry): MenuPlacement {
  const below = geometry.viewportHeight - geometry.anchorBottom - GAP - MARGIN;
  const above = geometry.anchorTop - GAP - MARGIN;

  // Flip only when it actually helps: a list that fits below stays below, so the
  // menu keeps the one position the partner's hand has learned.
  const flipped = geometry.listHeight > below && above > below;

  const available = geometry.viewportHeight - 2 * MARGIN;
  const room = Math.min(available, Math.max(flipped ? above : below, MIN_HEIGHT));
  const height = Math.min(geometry.listHeight, room);
  const maxHeight = height < geometry.listHeight ? height : null;

  const wanted = flipped ? geometry.anchorTop - GAP - height : geometry.anchorBottom + GAP;
  const top = clamp(wanted, MARGIN, geometry.viewportHeight - height - MARGIN);

  // Horizontally the correction is a delta: the transform is CSS's, so we move
  // the inline coordinate by however far the painted box has to travel.
  const paintedLeft = clamp(
    geometry.listLeft,
    MARGIN,
    geometry.viewportWidth - geometry.listWidth - MARGIN,
  );
  const left = geometry.inlineLeft + (paintedLeft - geometry.listLeft);

  return { top, left, maxHeight };
}
